import { AuthenticationFailedError } from "@/auth/AuthError";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useAppState } from "@/store/useApp";
import { AssessmentInfo } from "@/types/assessment";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { AuthSession } from "@/types/auth";

type LaunchParams = {
  code: string | null;
  courseId: string | null;
  deepLinkSessionId: string | null;
};

const ltiExchangePromises = new Map<string, Promise<AuthSession>>();

export const LtiLaunch = () => {
  const location = useLocation();
  const { acceptSession, clearSession } = useAuth();
  const { completeLtiDeepLink, exchangeLtiCode, fetchMe } = useApi();
  const courses = useAppState((state) => state.courses);
  const assessments = useAppState((state) => state.assessments);
  const [error, setError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [isLaunchReady, setIsLaunchReady] = useState(false);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<string | null>(
    null
  );
  const [isSubmittingSelection, setIsSubmittingSelection] = useState(false);
  const validationCompletedRef = useRef(false);
  const fetchMeRef = useRef(fetchMe);
  const exchangeLtiCodeRef = useRef(exchangeLtiCode);

  const { code, courseId, deepLinkSessionId } = useMemo<LaunchParams>(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      code: searchParams.get("code"),
      courseId: searchParams.get("course_id"),
      deepLinkSessionId: searchParams.get("deep_link_session_id"),
    };
  }, [location.search]);

  const isDeepLinkLaunch = Boolean(courseId && deepLinkSessionId);

  const selectedCourse = useMemo(
    () => courses?.find((course) => course.id === courseId) ?? null,
    [courseId, courses]
  );

  const courseAssessments = useMemo(() => {
    if (!courseId || !assessments) {
      return [];
    }

    return assessments
      .filter((assessment) => assessment.courseId === courseId)
      .sort(sortAssessments);
  }, [assessments, courseId]);

  useEffect(() => {
    fetchMeRef.current = fetchMe;
  }, [fetchMe]);

  useEffect(() => {
    exchangeLtiCodeRef.current = exchangeLtiCode;
  }, [exchangeLtiCode]);

  useEffect(() => {
    let cancelled = false;

    const validateLaunch = async () => {
      if (!code) {
        setError("The LTI callback did not include a launch code.");
        setIsValidating(false);
        return;
      }

      if (validationCompletedRef.current) {
        setIsLaunchReady(true);
        setIsValidating(false);
        return;
      }

      validationCompletedRef.current = false;

      setError(null);
      setIsLaunchReady(false);
      setIsValidating(true);

      try {
        let exchangePromise = ltiExchangePromises.get(code);
        if (!exchangePromise) {
          exchangePromise = exchangeLtiCodeRef.current(code).catch((error) => {
            ltiExchangePromises.delete(code);
            throw error;
          });
          ltiExchangePromises.set(code, exchangePromise);
        }

        const session = await exchangePromise;
        acceptSession(session);
        await fetchMeRef.current({ suppressAuthRedirect: true });
        validationCompletedRef.current = true;
        if (!cancelled) {
          setIsLaunchReady(true);
          setIsValidating(false);
        }
      } catch (launchError) {
        clearSession();
        validationCompletedRef.current = false;

        if (!cancelled) {
          setError(getLaunchErrorMessage(launchError));
          setIsValidating(false);
        }
      }
    };

    void validateLaunch();

    return () => {
      cancelled = true;
    };
  }, [acceptSession, clearSession, code]);

  useEffect(() => {
    if (isDeepLinkLaunch) {
      return;
    }

    if (!isLaunchReady) {
      return;
    }

    window.location.replace("/");
  }, [isDeepLinkLaunch, isLaunchReady]);

  useEffect(() => {
    if (!selectedAssessmentId && courseAssessments.length > 0) {
      setSelectedAssessmentId(courseAssessments[0].id);
    }
  }, [courseAssessments, selectedAssessmentId]);

  const handleCompleteDeepLink = async () => {
    if (!deepLinkSessionId || !selectedAssessmentId) {
      return;
    }

    setError(null);
    setIsSubmittingSelection(true);

    try {
      const responseHtml = await completeLtiDeepLink({
        deep_link_session_id: deepLinkSessionId,
        assessment_id: selectedAssessmentId,
      });
      submitDeepLinkResponse(responseHtml);
    } catch (submitError) {
      setError(getLaunchErrorMessage(submitError));
    } finally {
      setIsSubmittingSelection(false);
    }
  };

  if (error && !isDeepLinkLaunch) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFEFD] p-4">
        <LaunchErrorCard error={error} />
      </div>
    );
  }

  if (!isDeepLinkLaunch) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFFEFD]">
        <p className="text-sm text-gray-600">
          {isValidating ? "Completing LTI sign-in..." : "Preparing launch..."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFEFD]">
      <Dialog open>
        <DialogContent
          className="h-screen max-h-screen max-w-none translate-x-[-50%] translate-y-[-50%] rounded-none border-0 p-0 shadow-none [&>button]:hidden"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <div className="flex h-full flex-col bg-[#FFFEFD]">
            <div className="mx-auto flex h-full w-full max-w-4xl flex-col px-6 py-10 sm:px-10">
              {isValidating ? (
                <LoadingState />
              ) : (
                <>
                  <DialogHeader className="border-b border-black/10 pb-6 text-left">
                    <DialogTitle className="text-2xl font-semibold text-black">
                      Select an assessment
                    </DialogTitle>
                    <DialogDescription className="max-w-2xl text-sm text-black/65">
                      {selectedCourse
                        ? `Choose the assessment to add to ${selectedCourse.title}.`
                        : "Choose the assessment to add to this course."}
                    </DialogDescription>
                  </DialogHeader>

                  <div className="flex-1 overflow-y-auto py-6">
                    {error ? (
                      <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                      </p>
                    ) : null}

                    {!selectedCourse ? (
                      <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        The selected course could not be found for this LTI deep
                        linking launch.
                      </p>
                    ) : courseAssessments.length === 0 ? (
                      <p className="rounded-lg border border-black/10 bg-white px-4 py-3 text-sm text-black/70">
                        No assessments are available for this course.
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {courseAssessments.map((assessment) => {
                          const isSelected =
                            assessment.id === selectedAssessmentId;

                          return (
                            <button
                              key={assessment.id}
                              type="button"
                              onClick={() => setSelectedAssessmentId(assessment.id)}
                              className={`w-full rounded-2xl border px-5 py-4 text-left transition ${
                                isSelected
                                  ? "border-primary bg-primary/5"
                                  : "border-black/10 bg-white hover:border-black/25"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-base font-medium text-black">
                                    {assessment.title}
                                  </p>
                                  <p className="mt-1 text-sm text-black/55">
                                    {formatAssessmentMeta(assessment)}
                                  </p>
                                </div>
                                <div
                                  className={`mt-1 h-4 w-4 rounded-full border ${
                                    isSelected
                                      ? "border-primary bg-primary"
                                      : "border-black/20 bg-white"
                                  }`}
                                />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-black/10 pt-6">
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="lg"
                        onClick={handleCompleteDeepLink}
                        disabled={
                          !selectedCourse ||
                          !selectedAssessmentId ||
                          isSubmittingSelection
                        }
                      >
                        {isSubmittingSelection ? (
                          <>
                            <Loader2 className="animate-spin" />
                            Selecting...
                          </>
                        ) : (
                          "Select"
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LaunchErrorCard = ({ error }: { error: string }) => {
  return (
    <div className="w-full max-w-lg space-y-4 rounded-xl border bg-white p-6 shadow-sm">
      <h1 className="text-xl font-semibold">Unable to launch Claire</h1>
      <p className="text-sm text-gray-600">{error}</p>
    </div>
  );
};

const LoadingState = () => {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <div className="space-y-1">
        <p className="text-base font-medium text-black">
          Completing LTI sign-in...
        </p>
        <p className="text-sm text-black/60">
          Preparing your assessment selection.
        </p>
      </div>
    </div>
  );
};

function getLaunchErrorMessage(error: unknown): string {
  if (error instanceof AuthenticationFailedError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "The LTI launch could not be completed.";
}

function submitDeepLinkResponse(html: string) {
  const parser = new DOMParser();
  const documentFragment = parser.parseFromString(html, "text/html");
  const returnedForm = documentFragment.querySelector("form");

  if (!returnedForm) {
    throw new Error("LTI deep link response did not include a form.");
  }

  const container = document.createElement("div");
  container.style.display = "none";
  container.innerHTML = html;
  document.body.appendChild(container);

  const form = container.querySelector("form");

  if (!form) {
    document.body.removeChild(container);
    throw new Error("LTI deep link response form could not be prepared.");
  }

  form.submit();
}

function formatAssessmentMeta(assessment: AssessmentInfo): string {
  const details = [
    assessment.type ? capitalize(assessment.type) : null,
    assessment.status ? capitalize(assessment.status) : null,
  ].filter(Boolean);

  return details.length > 0 ? details.join(" • ") : "Assessment";
}

function sortAssessments(a: AssessmentInfo, b: AssessmentInfo): number {
  const aDate = new Date(a.createdAt).getTime();
  const bDate = new Date(b.createdAt).getTime();
  return bDate - aDate;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
